import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  Calendar,
  ChevronDown,
  Copy,
  FileText,
  Hash,
  TrendingUp,
  User,
  Wallet,
} from 'lucide-react';
import { GenericTransactionReceipt } from '@/components/transactions/GenericTransactionReceipt';
import {
  AdminDetailRow,
  AdminDetailSection,
  AdminInfoField,
  AdminInfoGrid,
} from '@/components/admin/AdminDetailSection';
import { ElectricityReceipt } from '@/components/receipts/ElectricityReceipt';
import { ExamReceipt } from '@/components/receipts/ExamReceipt';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import type { AdminTheme } from '@/context/AdminThemeContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminTransactionService, getApiErrorMessage } from '@/services';
import type { TransactionItem } from '@/types/transactions';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import {
  formatTransactionAmount,
  formatTransactionNaira,
  getCategoryTone,
  getTransactionStatusTone,
  transactionToBillReceipt,
} from '@/utils/transactionFormatters';
import { notify } from '@/utils/notify';

function StatusBadge({ status }: { status: TransactionItem['status'] }) {
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize', getTransactionStatusTone(status))}>
      {status}
    </span>
  );
}

function CategoryBadge({ transaction }: { transaction: TransactionItem }) {
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase', getCategoryTone(transaction.category))}>
      {transaction.categoryLabel}
    </span>
  );
}

function TypeBadge({ type, theme }: { type: TransactionItem['type']; theme: AdminTheme }) {
  const dark = theme === 'dark';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
        type === 'credit'
          ? 'bg-emerald-500/15 text-emerald-500'
          : dark
            ? 'bg-slate-500/20 text-slate-300'
            : 'bg-slate-200 text-slate-700',
      )}
    >
      {type}
    </span>
  );
}

function copyText(text: string) {
  void navigator.clipboard.writeText(text).then(() => notify.success('Copied to clipboard'));
}

export function AdminTransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);
  const [loading, setLoading] = useState(true);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [transaction, setTransaction] = useState<TransactionItem | null>(null);

  const load = useCallback(async () => {
    const txId = Number(id);
    if (!Number.isInteger(txId) || txId <= 0) return;
    setLoading(true);
    try {
      const res = await adminTransactionService.getById(txId);
      setTransaction(res.data?.transaction ?? null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load transaction'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <Loader label="Loading transaction..." />;
  }

  if (!transaction) {
    return (
      <Card className={cn('p-8 text-center', shell.card)}>
        <p className={shell.cardMuted}>Transaction not found.</p>
        <Link to={ROUTES.adminTransactions} className="mt-4 inline-block text-primary hover:underline">
          Back to transactions
        </Link>
      </Card>
    );
  }

  const billReceipt = transactionToBillReceipt(transaction);
  const userInitials = transaction.user
    ? `${transaction.user.firstName.charAt(0)}${transaction.user.lastName.charAt(0)}`.toUpperCase()
    : '?';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Link
            to={ROUTES.adminTransactions}
            className={cn('mt-0.5 inline-flex rounded-xl border p-2.5 transition-colors', shell.outlineBtn)}
            aria-label="Back to transactions"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <p className={cn('text-xs font-semibold uppercase tracking-wider', shell.cardMuted)}>Transaction</p>
            <h1 className="text-xl font-bold sm:text-2xl">{transaction.serviceName}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <CategoryBadge transaction={transaction} />
              <StatusBadge status={transaction.status} />
              <TypeBadge type={transaction.type} theme={theme} />
            </div>
            <button
              type="button"
              onClick={() => copyText(transaction.reference)}
              className={cn('mt-2 inline-flex items-center gap-1.5 font-mono text-xs', shell.cardMuted, 'hover:text-primary')}
            >
              <Hash className="h-3.5 w-3.5" />
              {transaction.reference}
              <Copy className="h-3 w-3 opacity-60" />
            </button>
          </div>
        </div>

        <div className={cn('rounded-2xl border px-5 py-4 text-right', shell.card)}>
          <p className={cn('text-xs font-medium', shell.cardMuted)}>Amount</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{formatTransactionAmount(transaction)}</p>
          {transaction.profit != null && transaction.profit > 0 && (
            <p className="mt-1 text-sm font-semibold text-emerald-500">
              +{formatTransactionNaira(transaction.profit)} profit
            </p>
          )}
        </div>
      </div>

      {/* Quick stats */}
      <AdminInfoGrid theme={theme} columns={2}>
        <AdminInfoField
          theme={theme}
          label="Profit"
          value={
            <span className="text-emerald-500">{formatTransactionNaira(transaction.profit ?? 0)}</span>
          }
        />
        <AdminInfoField theme={theme} label="Provider cost" value={formatTransactionNaira(transaction.costPrice ?? 0)} />
        <AdminInfoField theme={theme} label="Balance before" value={formatTransactionNaira(transaction.balanceBefore)} />
        <AdminInfoField theme={theme} label="Balance after" value={formatTransactionNaira(transaction.balanceAfter)} />
      </AdminInfoGrid>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <AdminDetailSection theme={theme} title="Transaction details" icon={FileText} iconClassName="text-blue-400">
            <div className="space-y-3">
              <AdminDetailRow theme={theme} label="Reference" value={transaction.reference} mono />
              <AdminDetailRow theme={theme} label="Service" value={transaction.serviceName} />
              <AdminDetailRow theme={theme} label="Kind" value={transaction.kindLabel} />
              {transaction.provider && (
                <AdminDetailRow theme={theme} label="Provider" value={transaction.provider} />
              )}
              {transaction.providerOrderNo && (
                <AdminDetailRow theme={theme} label="Provider order" value={transaction.providerOrderNo} mono />
              )}
              {transaction.description && (
                <AdminDetailRow theme={theme} label="Description" value={transaction.description} />
              )}
              <AdminDetailRow
                theme={theme}
                label="Created"
                value={new Date(transaction.createdAt).toLocaleString('en-NG', {
                  dateStyle: 'full',
                  timeStyle: 'short',
                })}
              />
            </div>
          </AdminDetailSection>

          <AdminDetailSection theme={theme} title="Financial breakdown" icon={Banknote} iconClassName="text-emerald-400">
            <div className="space-y-3">
              <AdminDetailRow theme={theme} label="Amount charged" value={formatTransactionAmount(transaction)} highlight />
              {transaction.faceValue != null && (
                <AdminDetailRow theme={theme} label="Face value" value={formatTransactionNaira(transaction.faceValue)} />
              )}
              <AdminDetailRow theme={theme} label="Provider cost" value={formatTransactionNaira(transaction.costPrice ?? 0)} />
              <AdminDetailRow
                theme={theme}
                label="Profit"
                value={<span className="text-emerald-500">{formatTransactionNaira(transaction.profit ?? 0)}</span>}
              />
              {transaction.discountPercent != null && transaction.discountPercent > 0 && (
                <AdminDetailRow theme={theme} label="Discount" value={`${transaction.discountPercent}%`} />
              )}
              {transaction.userTypeAtPurchase && (
                <AdminDetailRow
                  theme={theme}
                  label="User type at purchase"
                  value={<span className="capitalize">{transaction.userTypeAtPurchase}</span>}
                />
              )}
            </div>
          </AdminDetailSection>

          <AdminDetailSection theme={theme} title="Wallet impact" icon={Wallet} iconClassName="text-cyan-400">
            <div className="space-y-3">
              <AdminDetailRow theme={theme} label="Balance before" value={formatTransactionNaira(transaction.balanceBefore)} />
              <AdminDetailRow theme={theme} label="Balance after" value={formatTransactionNaira(transaction.balanceAfter)} />
              <AdminDetailRow
                theme={theme}
                label="Affects balance"
                value={transaction.affectsBalance ? 'Yes' : 'No'}
              />
            </div>
          </AdminDetailSection>
        </div>

        <div className="space-y-4">
          <AdminDetailSection theme={theme} title="Customer" icon={User} iconClassName="text-violet-400">
            {transaction.user ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
                    {userInitials}
                  </div>
                  <div>
                    <p className="font-semibold">
                      {transaction.user.firstName} {transaction.user.lastName}
                    </p>
                    <p className={cn('text-xs capitalize', shell.cardMuted)}>{transaction.user.userType}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <AdminDetailRow theme={theme} label="Phone" value={transaction.user.phone} />
                  <AdminDetailRow theme={theme} label="Email" value={transaction.user.email} />
                </div>
                <Link
                  to={`/admin/users/${transaction.user.id}`}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary/90"
                >
                  View user profile
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <p className={cn('text-sm', shell.cardMuted)}>User details unavailable</p>
            )}
          </AdminDetailSection>

          <AdminDetailSection theme={theme} title="Timeline" icon={Calendar} iconClassName="text-amber-400">
            <div className="space-y-3">
              <AdminDetailRow
                theme={theme}
                label="Created"
                value={new Date(transaction.createdAt).toLocaleString('en-NG', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              />
              <AdminDetailRow
                theme={theme}
                label="Updated"
                value={new Date(transaction.updatedAt).toLocaleString('en-NG', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              />
            </div>
          </AdminDetailSection>
        </div>
      </div>

      {/* Receipt */}
      <AdminDetailSection theme={theme} title="Receipt preview" icon={TrendingUp} iconClassName="text-primary">
        <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-1 shadow-lg shadow-black/10">
          {billReceipt?.type === 'electricity' ? (
            <ElectricityReceipt receipt={billReceipt} />
          ) : billReceipt?.type === 'exam' ? (
            <ExamReceipt receipt={billReceipt} />
          ) : (
            <GenericTransactionReceipt transaction={transaction} showProfit />
          )}
        </div>
      </AdminDetailSection>

      {/* Metadata */}
      <Card className={cn('overflow-hidden', shell.card)}>
        <button
          type="button"
          onClick={() => setMetadataOpen((open) => !open)}
          className={cn(
            'flex w-full items-center justify-between px-4 py-3.5 text-left sm:px-5',
            'transition-colors hover:bg-slate-800/40',
          )}
        >
          <span className="text-sm font-bold">Raw metadata</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform', metadataOpen && 'rotate-180', shell.cardMuted)} />
        </button>
        {metadataOpen && (
          <pre
            className={cn(
              'max-h-96 overflow-auto border-t px-4 py-4 text-xs leading-relaxed sm:px-5',
              shell.tableWrap,
              theme === 'dark' ? 'bg-slate-950 text-emerald-300' : 'bg-brand-surface text-brand-navy',
            )}
          >
            {JSON.stringify(transaction.metadata, null, 2)}
          </pre>
        )}
      </Card>
    </div>
  );
}
